<template>
  <div>
    <error-component v-if="error" :message="error" v-on:clear="clear()" />
    <template v-if="learnerLoaded">
      <DashboardAccelerate
        v-if="checkIsAcceleratePartner()"
        :refreshDashboardPage="refreshDashboardPage"
        :pageLoad="pageLoad"
      />
      <Dashboard
        v-else
        :refreshDashboardPage="refreshDashboardPage"
        :pageLoad="pageLoad"
        :dashboardSectionsLoad="dashboardSectionsLoad"
      />
    </template>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapState } from 'pinia';
import * as Sentry from '@sentry/vue';
import { useUserStore } from '@/stores/user';

import Dashboard from './Dashboard.vue';
import DashboardAccelerate from './DashboardAccelerate.vue';
import { Helper } from '../mixins/helpers.js';
import { SessionInitializer } from '../mixins/sessionInitializer';

export default defineComponent({
  name: 'DashboardWrapper',
  mixins: [Helper, SessionInitializer],

  components: {
    Dashboard,
    DashboardAccelerate
  },

  props: {
    refreshDashboardPage: {
      type: Object
    }
  },

  data() {
    return {
      // Which dashboard to render depends on the learner's partner, so neither renders
      // until the learner is known; each then waits on this one load instead of its own.
      learnerLoaded: false,
      pageLoad: null,
      dashboardSectionsLoad: null,
      // Whose data the two loads hold: a view-as switch to another learner retires them.
      loadLearnerId: null,
      // Set by init when the session cannot load; Dashboard showed it before the load moved here.
      error: ''
    };
  },

  watch: {
    // A dashboard mounted after the switch (standard <-> Accelerate) then loads for the new
    // learner itself; one that stays mounted refreshes through its own view-as watcher.
    effectiveLearnerId(learnerId) {
      if (this.loadLearnerId && learnerId !== this.loadLearnerId) {
        this.pageLoad = null;
        this.dashboardSectionsLoad = null;
      }
    }
  },

  async created() {
    this.detectStripeRedirectLanding();

    // From checkout completion: drop the cached learner so the load below fetches fresh data.
    if (this.$route.query.refreshEnrollments) {
      this.$conn.learnerData = null;

      const query = { ...this.$route.query };
      delete query.refreshEnrollments;
      this.$router.replace({ name: 'dashboard', query });
    }

    this.pageLoad = new Promise((resolve) => {
      this.init('dashboard', resolve, () => {
        // The standard dashboard's pathways and certificates read, started alongside the
        // enrollments fetch as it was before the load moved up here.
        if (!this.checkIsAcceleratePartner()) {
          this.dashboardSectionsLoad = this.$conn.getDashboard();
          this.dashboardSectionsLoad.catch(() => {});
        }

        this.loadLearnerId = this.effectiveLearnerId;
        this.learnerLoaded = true;
      }).catch((err) => {
        this.logError('dashboard page load failed', err);
        this.$router.push({ name: 'server-error' });
      });
    });
  },

  computed: {
    ...mapState(useUserStore, ['getLearnerData']),

    effectiveLearnerId() {
      return this.getLearnerData?._learnerId || this.getLearnerData?.learnerId || null;
    }
  },

  methods: {
    clear() {
      this.$router.push({ name: 'login' });
    },

    checkIsAcceleratePartner() {
      const learnerData = this.getLearnerData;

      return this.isAcceleratePartner(learnerData?._partnerURLSubDomain);
    },

    // Detect when a Stripe redirect-based payment (e.g. 3DS step-up) returned
    // the learner to /dashboard. The post-payment enrollment upgrade does not
    // run on this route, so flag it to Sentry for manual reconciliation.
    detectStripeRedirectLanding() {
      const query = this.$route?.query || {};
      const hasStripeRedirectParams = !!(
        query.payment_intent ||
        query.payment_intent_client_secret ||
        query.redirect_status
      );

      if (!hasStripeRedirectParams) return;

      // Deduplicate per payment_intent so a refresh or bfcache restore (which
      // keeps the Stripe redirect params in the URL) doesn't re-fire the alert.
      const dedupeKey = `stripe_redirect_landing:${query.payment_intent || query.redirect_status || 'unknown'}`;
      try {
        if (sessionStorage.getItem(dedupeKey)) return;
        sessionStorage.setItem(dedupeKey, '1');
      } catch {
        // sessionStorage unavailable (private mode/quota) — fall through and alert anyway.
      }

      const learnerData = this.getLearnerData || {};

      Sentry.captureMessage('Stripe redirect payment landed on /dashboard — enrollment upgrade likely skipped', {
        level: 'warning',
        tags: {
          source: 'stripe_redirect_landing',
          redirect_status: query.redirect_status || 'unknown'
        },
        extra: {
          paymentIntent: query.payment_intent,
          redirectStatus: query.redirect_status,
          learnerId: learnerData._id
        }
      });
    }
  }
});
</script>
